import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Logo from '../components/Logo';
import { CheckCircle, X, Sparkle, Crown, Storefront } from '@phosphor-icons/react';

const BILLING_CYCLES = [
  { id: 'monthly', label: 'Månedlig', discount: 0, freeSetup: false, badge: null },
  { id: 'quarterly', label: '3 mnd', discount: 0.05, freeSetup: false, badge: '-5%' },
  { id: 'yearly', label: 'Årlig', discount: 0.15, freeSetup: true, badge: '-15% + Gratis etablering' },
];

const TIERS = [
  {
    id: 'starter',
    name: 'Starter',
    icon: '🌱',
    tagline: 'Perfekt for små nettbutikker som vil komme i gang.',
    monthlyPrice: 399,
    setupFee: 990,
    quota: 1500,
    rate: 50,
    stores: 1,
    features: [
      '1 500 optimaliseringer/mnd',
      '1 butikk',
      '50 produkter/time',
      'AutoGuard SEO',
      'Mediabibliotek-rydding',
      'Broken Links scanner',
      'Yoast & RankMath støtte',
      'E-postsupport',
    ],
    missing: ['Google Search Console', 'Re-optimaliser via SEO Insights', 'PDF-rapport'],
  },
  {
    id: 'pro',
    name: 'Pro',
    icon: '🚀',
    tagline: 'For voksende nettbutikker som vil maksimere SEO.',
    monthlyPrice: 1299,
    setupFee: 1990,
    quota: 15000,
    rate: 200,
    stores: 1,
    highlight: true,
    features: [
      '15 000 optimaliseringer/mnd',
      '1 butikk',
      '200 produkter/time',
      'Alt i Starter +',
      'Google Search Console + GA4',
      'SEO Insights med Re-optimaliser',
      'PDF-rapport',
      'Prioritert support',
    ],
    missing: ['Multi-store', 'White-label'],
  },
  {
    id: 'agency_s',
    name: 'Agency Small',
    icon: '🏢',
    tagline: 'For byråer som driver flere butikker.',
    monthlyPrice: 2999,
    setupFee: 4990,
    quota: 50000,
    rate: 300,
    stores: 3,
    features: [
      '50 000 optimaliseringer per butikk/mnd',
      'Inntil 3 butikker',
      '300 produkter/time',
      'Alt i Pro +',
      'White-label rapporter',
      'Slack-kanal med support',
    ],
    missing: [],
  },
  {
    id: 'agency_m',
    name: 'Agency Medium',
    icon: '🏢',
    tagline: 'For etablerte byråer.',
    monthlyPrice: 4999,
    setupFee: 4990,
    quota: 50000,
    rate: 300,
    stores: 8,
    features: [
      '50 000 optimaliseringer per butikk/mnd',
      'Inntil 8 butikker',
      '300 produkter/time',
      'Alt i Agency Small',
      'Onboarding-samtale',
    ],
    missing: [],
  },
  {
    id: 'agency_l',
    name: 'Agency Large',
    icon: '🏢',
    tagline: 'For store byråer med mange merker.',
    monthlyPrice: 8999,
    setupFee: 4990,
    quota: 50000,
    rate: 300,
    stores: 20,
    features: [
      '50 000 optimaliseringer per butikk/mnd',
      'Inntil 20 butikker',
      '300 produkter/time',
      'Alt i Agency Medium',
      'Dedikert kontakt',
    ],
    missing: [],
  },
];

const TOPUP_PACKS = [
  { units: 500, price: 99 },
  { units: 2000, price: 299 },
  { units: 8000, price: 799 },
];

export default function PricingPage() {
  const [cycle, setCycle] = useState('monthly');
  const cycleData = BILLING_CYCLES.find((c) => c.id === cycle);

  const computePrice = (monthlyPrice) => {
    const discounted = monthlyPrice * (1 - cycleData.discount);
    return Math.round(discounted);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="pricing-page">
      <nav className="border-b border-border/50 backdrop-blur-sm bg-background/80 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="pricing-logo">
            <Logo variant="full" height={36} />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm hover:text-primary">Hjem</Link>
            <Link to="/login">
              <Button variant="outline" size="sm">Logg inn</Button>
            </Link>
          </div>
        </div>
      </nav>

      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-primary mb-3">Priser</p>
          <h1 className="font-heading text-5xl font-extrabold tracking-tight">Velg planen som passer</h1>
          <p className="text-muted-foreground mt-4">
            Norske priser i NOK, eks. MVA. Bytt periode for å se rabatter.
          </p>
        </div>

        {/* Billing cycle toggle */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-muted rounded-full p-1" data-testid="billing-cycle-toggle">
            {BILLING_CYCLES.map((c) => (
              <button
                key={c.id}
                onClick={() => setCycle(c.id)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
                  cycle === c.id ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`cycle-${c.id}`}
              >
                {c.label}
                {c.badge && (
                  <Badge variant="default" className="text-[10px] py-0 px-1.5 h-4">{c.badge}</Badge>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {TIERS.slice(0, 3).map((t) => (
            <TierCard key={t.id} tier={t} cycle={cycleData} computePrice={computePrice} />
          ))}
        </div>

        {/* Agency pakker */}
        <div className="mt-16">
          <h2 className="font-heading text-3xl font-extrabold text-center mb-2">
            <Storefront size={32} weight="bold" className="inline mr-2" />
            Agency-pakker
          </h2>
          <p className="text-center text-muted-foreground mb-8">For byråer som driver flere butikker.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {TIERS.slice(2).map((t) => (
              <TierCard key={t.id} tier={t} cycle={cycleData} computePrice={computePrice} />
            ))}
          </div>
        </div>

        {/* Top-up section */}
        <div className="mt-16">
          <h2 className="font-heading text-3xl font-extrabold text-center mb-2">💎 Topp-opp</h2>
          <p className="text-center text-muted-foreground mb-8">Kjøp ekstra optimaliseringer når du trenger det. Engangskjøp.</p>
          <div className="grid md:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {TOPUP_PACKS.map((p, i) => (
              <Card key={i} className="border" data-testid={`topup-${p.units}`}>
                <CardContent className="p-6 text-center">
                  <p className="font-heading text-3xl font-extrabold">{p.units.toLocaleString('nb-NO')}</p>
                  <p className="text-xs text-muted-foreground mb-3">optimaliseringer</p>
                  <p className="font-heading text-2xl font-bold text-primary">kr {p.price}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    kr {(p.price / p.units).toFixed(2)} per stk
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Premium AI pack */}
        <div className="mt-16 max-w-3xl mx-auto">
          <Card className="border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-pink-50">
            <CardContent className="p-8 text-center">
              <Crown size={32} weight="fill" className="text-amber-500 mx-auto mb-3" />
              <h3 className="font-heading text-2xl font-extrabold">Premium AI Pack</h3>
              <p className="text-sm text-muted-foreground mt-2 mb-4">
                Tilgjengelig som tillegg på alle planer. Bytter AI-modellen til <strong>Claude Sonnet 4.5</strong> (premium-kvalitet) for enda bedre språk, SEO-tone og HTML-struktur.
              </p>
              <p className="font-heading text-3xl font-extrabold text-amber-700">+ kr 199 / mnd</p>
            </CardContent>
          </Card>
        </div>

        {/* Footer note */}
        <div className="mt-16 text-center text-xs text-muted-foreground space-y-1 max-w-2xl mx-auto">
          <p>Alle priser i norske kroner (NOK), eks. MVA.</p>
          <p>1 optimalisering = 1 produkt, 1 kategori eller 1 attributtside. Re-optimaliseringer telles som ½ enhet.</p>
          <p>Etableringsavgift faktureres ved oppstart, og er gratis ved årsbinding.</p>
        </div>
      </section>
    </div>
  );
}

function TierCard({ tier, cycle, computePrice }) {
  const monthly = computePrice(tier.monthlyPrice);
  const setup = cycle.freeSetup ? 0 : tier.setupFee;
  return (
    <Card
      className={`relative ${tier.highlight ? 'border-2 border-primary shadow-xl' : 'border border-border'}`}
      data-testid={`tier-${tier.id}`}
    >
      {tier.highlight && (
        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 gap-1">
          <Sparkle size={10} weight="fill" /> Mest populær
        </Badge>
      )}
      <CardContent className="p-8">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-2xl">{tier.icon}</span>
          <h3 className="font-heading text-2xl font-bold">{tier.name}</h3>
        </div>
        <p className="text-sm text-muted-foreground mt-1 mb-6">{tier.tagline}</p>

        <div className="flex items-baseline gap-1">
          <span className="font-heading text-5xl font-extrabold" data-testid={`tier-price-${tier.id}`}>
            kr {monthly.toLocaleString('nb-NO')}
          </span>
          <span className="text-muted-foreground">/mnd</span>
        </div>
        {cycle.discount > 0 && (
          <p className="text-xs text-muted-foreground line-through">
            kr {tier.monthlyPrice.toLocaleString('nb-NO')} ord. pris
          </p>
        )}
        <div className="mt-2 mb-4">
          {setup > 0 ? (
            <p className="text-xs text-muted-foreground">
              + kr {setup.toLocaleString('nb-NO')} etablering (engang)
            </p>
          ) : (
            <p className="text-xs text-success font-medium">
              ✓ Gratis etablering (årsbinding)
            </p>
          )}
        </div>

        <ul className="space-y-2 mb-8 text-sm">
          {tier.features.map((f, i) => (
            <li key={`f${i}`} className="flex items-start gap-2">
              <CheckCircle size={16} weight="fill" className="text-success shrink-0 mt-0.5" />
              <span>{f}</span>
            </li>
          ))}
          {tier.missing.map((f, i) => (
            <li key={`m${i}`} className="flex items-start gap-2 text-muted-foreground/60">
              <X size={16} className="shrink-0 mt-0.5" />
              <span className="line-through">{f}</span>
            </li>
          ))}
        </ul>

        <Link to="/#waitlist">
          <Button
            className="w-full"
            variant={tier.highlight ? 'default' : 'outline'}
            size="lg"
            data-testid={`tier-cta-${tier.id}`}
          >
            Kom i gang
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}
